import { humanize } from './format.ts'

/** Presentation of the whole subject selected by a saved property card. */
export interface PropertyClassificationFacts {
  assetClass?: string | null
  propertyType?: string | null
  dwellingType?: string | null
  builtForm?: string | null
  bedrooms?: number | null
}

/**
 * A saved selection is authoritative as a whole, including null or redacted
 * fields. Raw listing fields are only the bootstrap before a card is available.
 * Residential components of a specialist lot must not label the whole lot.
 */
export function effectivePropertyClassification(raw: PropertyClassificationFacts, selected?: PropertyClassificationFacts | null) {
  const subject = selected ?? raw
  const assetClass = subject.assetClass ?? null
  const specialist = assetClass !== null && ['mixed_use', 'commercial', 'land', 'other'].includes(assetClass)
  return {
    assetClass,
    propertyType: specialist ? null : subject.propertyType ?? null,
    dwellingType: specialist ? null : subject.dwellingType ?? null,
    builtForm: specialist ? null : subject.builtForm ?? null,
    bedrooms: specialist ? null : subject.bedrooms ?? null,
  }
}

/** Missing or permission-redacted classification has a visible, neutral label. */
export function propertyClassificationLabel(subject: PropertyClassificationFacts, dwellingFirst = false): string {
  const classification = effectivePropertyClassification(subject)
  const type = dwellingFirst
    ? classification.dwellingType ?? classification.propertyType
    : classification.propertyType ?? classification.dwellingType
  return humanize(type ?? classification.assetClass) || 'Unclassified'
}

function rightmovePropertyType(value: string | null | undefined): string | null {
  const normalized = value?.toLocaleLowerCase('en-GB') ?? ''
  if (normalized.includes('semi')) return 'SEMI_DETACHED'
  if (normalized.includes('detached')) return 'DETACHED'
  if (normalized.includes('terrace')) return 'TERRACED'
  if (['flat', 'apartment', 'maisonette'].some(kind => normalized.includes(kind))) return 'FLAT'
  return null
}

/** The public postcode sold-prices search; only a selected dwelling adds a type filter. */
export function soldPricesUrl(postcode: string | null | undefined, subject: PropertyClassificationFacts): string | null {
  if (!postcode?.trim()) return null
  const location = postcode.trim().toLocaleLowerCase('en-GB').replace(/\s+/g, '-')
  const filters = new URLSearchParams({ radius: '0.5', soldIn: '2' })
  const classification = effectivePropertyClassification(subject)
  const type = rightmovePropertyType(classification.propertyType ?? classification.dwellingType)
  if (type) filters.set('propertyType', type)
  return `https://www.rightmove.co.uk/house-prices/${encodeURIComponent(location)}.html?${filters}`
}
